import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, ShieldAlert, ShieldCheck } from "lucide-react";

export default function Inbox() {
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [serial, setSerial] = useState("");
  const [wo, setWo] = useState("");
  const [channel, setChannel] = useState("");
  const [siteId, setSiteId] = useState("");
  const [sites, setSites] = useState([]);
  const [settings, setSettings] = useState({});
  const [force, setForce] = useState(false);

  const canIP2 = ["operations_controller", "warehouse_manager", "system_admin"].includes(user.role);
  const canSettings = user.role === "system_admin";

  const load = useCallback(() => {
    api.get("/inbox").then((r) => setEmails(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setSites(r.data.filter((l) => ["fitment_centre", "technician"].includes(l.type))));
  }, [load]);

  const toggleStrict = async (checked) => {
    try {
      const { data } = await api.put("/settings", { enforce_fitment_before_activation: checked });
      setSettings(data);
      toast.success(checked ? "Strict mode ON — activations without a captured fitment will be blocked" : "Strict mode OFF — IP2 will self-heal missing fitments");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ip2 = async () => {
    if (!serial) { toast.error("Serial required"); return; }
    try {
      const { data } = await api.post("/inbox/ip2", {
        serial, work_order_no: wo || null, channel: channel || null, site_id: siteId || null, force,
      });
      if (data.backfilled) {
        toast.success(`IP2 activated ${serial} — ledger reconciled (back-filled ${data.skipped_stages.join(", ")})`);
      } else {
        toast.success("IP2 received — unit activated");
      }
      setSerial(""); setWo(""); setChannel(""); setSiteId(""); setForce(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const strict = !!settings.enforce_fitment_before_activation;

  return (
    <div>
      <PageHeader title="AX Inbox (Simulated)" subtitle="The two AX → mailbox notifications. IP1 loads stock (via Stock Ledger); IP2 activates a unit — and self-heals the ledger if receipt/fitment was skipped." />

      <Panel className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="strict-fitment-panel">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: strict ? "#fef2f2" : "#f1f5f9" }}>
              {strict ? <ShieldCheck className="h-5 w-5" style={{ color: "#dc2626" }} /> : <ShieldAlert className="h-5 w-5" style={{ color: "#64748b" }} />}
            </div>
            <div>
              <div className="font-head font-semibold text-sm text-[#0B2545]">Require in-app fitment before activation</div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                When ON, IP2 will <b>block</b> any activation where no in-app fitment was captured — the unit stays put and must have its fitment recorded first.
                Genuine Mobitech field scans can still be pushed using the override below. When OFF, IP2 auto-reconstructs the missing stages and raises a Reconciliation flag (current behaviour).
              </p>
              <div className="mt-1.5">
                <span data-testid="strict-fitment-status" className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide"
                  style={strict ? { background: "#fef2f2", color: "#dc2626" } : { background: "#f1f5f9", color: "#475569" }}>
                  {strict ? "Strict — fitment required" : "Self-heal (flag on Recon)"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch data-testid="strict-fitment-toggle" checked={strict} disabled={!canSettings} onCheckedChange={toggleStrict} />
            {!canSettings && <span className="text-[11px] text-slate-400">System Admin only</span>}
          </div>
        </div>
      </Panel>

      {canIP2 && (
        <Panel title="Trigger IP2 — Unit Activated" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div><Label className="overline text-slate-500">Serial</Label><Input data-testid="ip2-serial" value={serial} onChange={(e) => setSerial(e.target.value)} className="rounded-sm mt-1 font-label" /></div>
            <div><Label className="overline text-slate-500">Work Order No. (optional)</Label><Input data-testid="ip2-wo" value={wo} onChange={(e) => setWo(e.target.value)} className="rounded-sm mt-1 font-label" /></div>
            <div><Label className="overline text-slate-500">Channel (optional)</Label><Input data-testid="ip2-channel" value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="e.g. Mobile / Dotsure" className="rounded-sm mt-1 font-label" /></div>
            <div><Label className="overline text-slate-500">Fitment centre (optional)</Label><div className="mt-1"><Select testid="ip2-site" value={siteId} onChange={setSiteId} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="Where consumed" /></div></div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-2 max-w-2xl">
              <p className="text-xs text-slate-400">If the unit was never booked in or captured as a fitment (e.g. a Mobitech field scan), IP2 will reconstruct the skipped stages, move the unit to the consuming centre and raise a reconciliation flag. Channel &amp; fitment centre help attribute custody.</p>
              {strict && (
                <label className="flex items-center gap-2 text-xs text-[#dc2626] font-head" data-testid="ip2-force-label">
                  <input type="checkbox" data-testid="ip2-force" checked={force} onChange={(e) => setForce(e.target.checked)} />
                  Override strict mode — this is a genuine Mobitech field scan (no in-app fitment)
                </label>
              )}
            </div>
            <Button data-testid="ip2-submit" onClick={ip2} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Activate (IP2)</Button>
          </div>
        </Panel>
      )}
      <Panel title="Mailbox">
        {emails.length === 0 ? <Empty>No AX emails yet. Trigger IP1 (Stock Ledger) to load stock.</Empty> : (
          <div className="space-y-2" data-testid="inbox-list">
            {emails.map((m) => (
              <div key={m.id} className="flex gap-3 border border-slate-100 rounded-sm p-3">
                <div className="shrink-0"><span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: m.type === "IP1" ? "#0B25451a" : "#16a34a1a", color: m.type === "IP1" ? "#0B2545" : "#16a34a" }}>{m.type}</span></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-head font-semibold text-[#0B2545] flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{m.subject}</div>
                  <div className="text-xs text-slate-500">{m.body}</div>
                  <div className="text-[10px] text-slate-400 mt-1 font-label">{new Date(m.received_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
